import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  X, 
  Trophy, 
  Medal, 
  DollarSign, 
  Users, 
  Clock,
  Award,
  Star,
  TrendingUp,
  Share2,
  Download,
  Eye,
  Crown,
  Flag
} from 'lucide-react';
import { 
  GroupCompetition, 
  CompetitionResults, 
  ParticipantResult,
  PrizeDistribution 
} from '../../types/group-competition.types';

interface GroupCompetitionResultsProps {
  group: GroupCompetition;
  results: CompetitionResults;
  onClose: () => void;
  onViewDetails: (participantId: string) => void;
}

// Animations
const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0,-30px,0);
  }
  70% {
    transform: translate3d(0,-15px,0);
  }
  90% {
    transform: scale(1.1);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TitleIcon = styled.span`
  font-size: 2.5rem;
  animation: ${bounce} 2s ease-in-out infinite;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: ${slideIn} 0.5s ease-out;
`;

const ResultsBanner = styled.div`
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #1e293b;
  padding: 30px;
  border-radius: 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -200px;
    width: 200px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: ${shimmer} 3s infinite;
  }
`;

const BannerTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const BannerSubtitle = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 20px;
  opacity: 0.9;
`;

const BannerStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
`;

const BannerStat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.8;
`;

const TieBreakerNotice = styled.div`
  background: rgba(255, 152, 0, 0.2);
  border: 2px solid #ff9800;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  margin-bottom: 30px;
`;

const TieBreakerTitle = styled.h3`
  color: #ff9800;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const TieBreakerText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
`;

const WinnersSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const WinnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const WinnerCard = styled.div<{ rank: number }>`
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, #ffd700, #ffed4e)';
    if (props.rank === 2) return 'linear-gradient(135deg, #c0c0c0, #e5e5e5)';
    if (props.rank === 3) return 'linear-gradient(135deg, #cd7f32, #daa520)';
    return 'rgba(255, 255, 255, 0.05)';
  }};
  border: 2px solid ${props => {
    if (props.rank === 1) return '#ffd700';
    if (props.rank === 2) return '#c0c0c0';
    if (props.rank === 3) return '#cd7f32';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 20px;
  padding: 25px;
  text-align: center;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const WinnerRank = styled.div<{ rank: number }>`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    if (props.rank === 1) return '#ffd700';
    if (props.rank === 2) return '#c0c0c0';
    if (props.rank === 3) return '#cd7f32';
    return '#9c27b0';
  }};
  color: ${props => props.rank === 2 ? '#333' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.2rem;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const WinnerAvatar = styled.div<{ rank: number }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, #ffd700, #ffed4e)';
    if (props.rank === 2) return 'linear-gradient(135deg, #c0c0c0, #e5e5e5)';
    if (props.rank === 3) return 'linear-gradient(135deg, #cd7f32, #daa520)';
    return 'linear-gradient(135deg, #9c27b0, #e91e63)';
  }};
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 2rem;
  color: ${props => props.rank === 2 ? '#333' : 'white'};
  border: 4px solid rgba(255, 255, 255, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const WinnerName = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.color || 'white'};
  margin-bottom: 15px;
`;

const WinnerScore = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 10px;
`;

const WinnerPrize = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #4caf50;
  margin-bottom: 15px;
`;

const WinnerStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const AllParticipantsSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const ParticipantsTable = styled.div`
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin-bottom: 15px;
  font-weight: 600;
  color: #ffd700;
  font-size: 0.9rem;
`;

const TableRow = styled.div<{ isWinner: boolean }>`
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 100px;
  gap: 15px;
  padding: 15px;
  background: ${props => props.isWinner 
    ? 'rgba(255, 215, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border-radius: 10px;
  margin-bottom: 10px;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

const RankCell = styled.div<{ rank: number }>`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${props => {
    if (props.rank === 1) return '#ffd700';
    if (props.rank === 2) return '#c0c0c0';
    if (props.rank === 3) return '#cd7f32';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  text-align: center;
`;

const NameCell = styled.div`
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ScoreCell = styled.div`
  font-weight: 600;
  color: #4caf50;
  text-align: center;
`;

const StatsCell = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
`;

const PrizeCell = styled.div`
  font-weight: 600;
  color: #4caf50;
  text-align: center;
`;

const ActionCell = styled.div`
  text-align: center;
`;

const ViewButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const SummarySection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
`;

const SummaryTitle = styled.h4`
  color: #ffd700;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 10px;
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const SummarySubtext = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 30px;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const GroupCompetitionResults: React.FC<GroupCompetitionResultsProps> = ({
  group,
  results,
  onClose,
  onViewDetails
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const winners = results.participants.filter(p => results.winners.includes(p.userId));
  const totalWinners = winners.length;
  const isTie = totalWinners > 1;

  const handleExportResults = async () => {
    setIsExporting(true);
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real app, this would generate and download a PDF/CSV
      const exportData = {
        groupName: group.name,
        competitionDate: results.completedAt,
        results: results.participants,
        winners: winners,
        totalPrizePool: results.totalPrizePool,
        appFee: group.appFeeAmount
      };
      
      console.log('Exporting results:', exportData);
      alert('Results exported successfully!');
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${group.name} - Competition Results`,
        text: `Check out the results of ${group.name} competition!`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown size={16} />;
    if (rank === 2) return <Medal size={16} />;
    if (rank === 3) return <Award size={16} />;
    return <Star size={16} />;
  };

  return (
    <ModalOverlay>
      <Header>
        <Title>
          <TitleIcon>🏆</TitleIcon>
          Competition Results
        </Title>
        <CloseButton onClick={onClose}>
          <X size={18} />
          Close
        </CloseButton>
      </Header>

      <ContentArea>
        {/* Results Banner */}
        <ResultsBanner>
          <BannerTitle>
            <Trophy size={40} />
            Competition Complete!
            <Trophy size={40} />
          </BannerTitle>
          <BannerSubtitle>
            {isTie 
              ? `${totalWinners} Winners - Prize Split Amongst All Winners`
              : 'Congratulations to our winner!'
            }
          </BannerSubtitle>
          
          <BannerStats>
            <BannerStat>
              <StatValue>{results.participants.length}</StatValue>
              <StatLabel>Participants</StatLabel>
            </BannerStat>
            <BannerStat>
              <StatValue>{totalWinners}</StatValue>
              <StatLabel>Winners</StatLabel>
            </BannerStat>
            <BannerStat>
              <StatValue>KES {results.totalPrizePool.toLocaleString()}</StatValue>
              <StatLabel>Total Prize Pool</StatLabel>
            </BannerStat>
            <BannerStat>
              <StatValue>KES {group.appFeeAmount.toLocaleString()}</StatValue>
              <StatLabel>App Fee (15%)</StatLabel>
            </BannerStat>
          </BannerStats>
        </ResultsBanner>

        {/* Tie Breaker Notice */}
        {isTie && (
          <TieBreakerNotice>
            <TieBreakerTitle>
              <Flag size={20} />
              Tie Breaker Applied
            </TieBreakerTitle>
            <TieBreakerText>
              Multiple participants achieved the same score! According to the competition rules, 
              the prize pool has been split equally among all winners. Each winner receives 
              KES {(results.totalPrizePool / totalWinners).toLocaleString()} 
              (less the 15% app fee).
            </TieBreakerText>
          </TieBreakerNotice>
        )}

        {/* Winners Section */}
        <WinnersSection>
          <SectionTitle>
            <Crown size={24} />
            {isTie ? 'Winners' : 'Winner'}
          </SectionTitle>
          
          <WinnersGrid>
            {winners.map((winner, index) => (
              <WinnerCard key={winner.userId} rank={winner.rank}>
                <WinnerRank rank={winner.rank}>{winner.rank}</WinnerRank>
                <WinnerAvatar rank={winner.rank}>
                  {winner.userName.charAt(0).toUpperCase()}
                </WinnerAvatar>
                
                <WinnerName color={winner.rank === 2 ? '#333' : 'white'}>
                  {winner.userName}
                </WinnerName>
                
                <WinnerScore>
                  Score: {winner.score} points
                </WinnerScore>
                
                <WinnerPrize>
                  KES {winner.prizeAmount.toLocaleString()}
                </WinnerPrize>
                
                <WinnerStats>
                  <div>Correct: {winner.correctAnswers}/{winner.totalQuestions}</div>
                  <div>Time: {formatTime(winner.timeTaken)}</div>
                </WinnerStats>
              </WinnerCard>
            ))}
          </WinnersGrid>
        </WinnersSection>

        {/* All Participants Results */}
        <AllParticipantsSection>
          <SectionTitle>
            <Users size={24} />
            All Participants Results
          </SectionTitle>
          
          <ParticipantsTable>
            <TableHeader>
              <div>Rank</div>
              <div>Participant</div>
              <div>Score</div>
              <div>Correct</div>
              <div>Time</div>
              <div>Prize</div>
              <div>Actions</div>
            </TableHeader>
            
            {results.participants.map((participant) => (
              <TableRow key={participant.userId} isWinner={results.winners.includes(participant.userId)}>
                <RankCell rank={participant.rank}>
                  {getRankIcon(participant.rank)}
                  {participant.rank}
                </RankCell>
                
                <NameCell>
                  <div style={{ 
                    width: '30px', 
                    height: '30px', 
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4caf50, #8bc34a)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {participant.userName.charAt(0).toUpperCase()}
                  </div>
                  {participant.userName}
                </NameCell>
                
                <ScoreCell>{participant.score}</ScoreCell>
                
                <StatsCell>
                  {participant.correctAnswers}/{participant.totalQuestions}
                </StatsCell>
                
                <StatsCell>
                  {formatTime(participant.timeTaken)}
                </StatsCell>
                
                <PrizeCell>
                  KES {participant.prizeAmount.toLocaleString()}
                </PrizeCell>
                
                <ActionCell>
                  <ViewButton onClick={() => onViewDetails(participant.userId)}>
                    <Eye size={14} />
                    View
                  </ViewButton>
                </ActionCell>
              </TableRow>
            ))}
          </ParticipantsTable>
        </AllParticipantsSection>

        {/* Competition Summary */}
        <SummarySection>
          <SectionTitle>
            <TrendingUp size={24} />
            Competition Summary
          </SectionTitle>
          
          <SummaryGrid>
            <SummaryCard>
              <SummaryTitle>Total Participants</SummaryTitle>
              <SummaryValue>{results.participants.length}</SummaryValue>
              <SummarySubtext>Started the competition</SummarySubtext>
            </SummaryCard>
            
            <SummaryCard>
              <SummaryTitle>Average Score</SummaryTitle>
              <SummaryValue>
                {Math.round(results.participants.reduce((sum, p) => sum + p.score, 0) / results.participants.length)}
              </SummaryValue>
              <SummarySubtext>Points per participant</SummarySubtext>
            </SummaryCard>
            
            <SummaryCard>
              <SummaryTitle>Highest Score</SummaryTitle>
              <SummaryValue>
                {Math.max(...results.participants.map(p => p.score))}
              </SummaryValue>
              <SummarySubtext>Best performance</SummarySubtext>
            </SummaryCard>
            
            <SummaryCard>
              <SummaryTitle>Competition Duration</SummaryTitle>
              <SummaryValue>
                {Math.round((new Date(results.completedAt).getTime() - new Date(group.competitionStartTime || 0).getTime()) / (1000 * 60))}m
              </SummaryValue>
              <SummarySubtext>Total time taken</SummarySubtext>
            </SummaryCard>
            
            <SummaryCard>
              <SummaryTitle>App Fee Collected</SummaryTitle>
              <SummaryValue>KES {group.appFeeAmount.toLocaleString()}</SummaryValue>
              <SummarySubtext>15% of total stakes</SummarySubtext>
            </SummaryCard>
            
            <SummaryCard>
              <SummaryTitle>Prize Distribution</SummaryTitle>
              <SummaryValue>
                {results.prizeDistribution.length > 0 
                  ? `${results.prizeDistribution.length} winners`
                  : 'Single winner'
                }
              </SummaryValue>
              <SummarySubtext>
                {isTie ? 'Split equally' : 'Winner takes all'}
              </SummarySubtext>
            </SummaryCard>
          </SummaryGrid>
        </SummarySection>

        {/* Action Buttons */}
        <ActionButtons>
          <ActionButton variant="primary" onClick={handleExportResults} disabled={isExporting}>
            <Download size={18} />
            {isExporting ? 'Exporting...' : 'Export Results'}
          </ActionButton>
          
          <ActionButton variant="secondary" onClick={handleShare}>
            <Share2 size={18} />
            Share Results
          </ActionButton>
        </ActionButtons>
      </ContentArea>
    </ModalOverlay>
  );
};

export default GroupCompetitionResults;
